/* KW 3.0 — variant picker, subscription add-on, and the inline CTAs.

   Two presentations, one engine. Every figure the buy box needs is rendered
   by Liquid onto a hidden radio — price, comparison basis, saving, piece
   count, parts total, contents summary, media id, selling-plan map — so this
   file does no money maths on the variant itself and cannot disagree with
   checkout.

   Radio mode: the radios are the visible control (any multi-variant product).

   Toggle mode: the customer sees add-on switches. Each switch owns a bit; the
   combination is a bitmask; each variant carries the mask it represents. Flip
   a switch, read the mask, find the radio, apply.

   ── The gallery is a track, not a frame ────────────────────
   The section renders every image as a slide in a scroll-snap track so the
   customer can swipe. So a variant change no longer rewrites an img src — it
   announces which media it wants and kw-gallery.js scrolls there.

   ── The subscription row is different ──────────────────────
   It is NOT a variant. The kit is hardware you buy once; subscribing to it
   would ship another two cannons and a mitt every few months. So the switch
   adds a SECOND cart line: the refill, on the selling plan the customer
   picks. That needs one /cart/add.js call carrying both items, which means
   taking the submit event off Dawn's product-form for that case only.

   The plan's FIRST cycle may be free — the kit already contains the liquids,
   so the subscription is meant to begin when they run out, not at checkout.
   Three prices therefore matter and no two of them are the same number:

     data-price / data-money  what the card is charged TODAY (£0 when free)
     data-recur               what it is charged EVERY cycle after
     data-was                 the product's own full price

   The button total and the price column use the first; the note uses the
   second. The third is not derivable from either — it belongs to the product,
   not the plan — so Liquid hands it over per option.

   ── Writing the terms ──────────────────────────────────────
   A free first cycle is the easiest thing on this page to misread, in both
   directions: a careless reader sees "free" and expects a gift in the box, a
   deliberate one reads it that way on purpose and asks where their refill is.
   So the note states the negatives outright before it gets to what does
   happen and when. Terms are the one place on the page where length is
   cheaper than ambiguity.

   "no charge, and no refill" is deliberately not "no refill in this box".
   The kit DOES contain two 500ml bottles — they are half of what it is — so
   pointing at the box trades one misreading for a worse one, and would have
   people opening a full kit convinced the foam was missing. The bare denial
   followed straight away by "First refill ships in 2 months" says what is not
   coming and when it is, without ever describing the box.

   The struck-through RRP is deliberately NOT shown beside a free first cycle.
   £45.00 crossed out above £0 is the same gift claim, made in figures. It
   belongs against the recurring charge it actually discounts, which is where
   the dropdown puts it.

   ── The inline CTAs are proxies ────────────────────────────
   The mid-page buttons used to carry their own form built from
   selected_or_first_available_variant, so they added the base variant at the
   base price however the switches were set. They now submit the real form.

   The button total is the sum of the cart lines, so it is the only place here
   that formats money in JavaScript. Everything else stays Liquid's job.
*/

(function () {
  var MDASH = String.fromCharCode(8212);
  var NBSP = String.fromCharCode(160);

  /* The standing promises, said once at the end of the note. Free delivery is
     a real part of the offer and belongs beside the price, not only in the
     subscription app's own copy where this page never shows it. */
  var SUB_TERMS = 'Free UK delivery. Pause or cancel any time.';

  function show(el, on) { if (el) el.hidden = !on; }

  /* Everything downstream of the buy box that has to agree with the chosen
     configuration: the kit table, and the piece count in the stat strip.
     Both live in other sections, so they are reached through the document. */
  function syncDownstream(d) {
    var mine = (d.kitRows || '').split(',');

    document.querySelectorAll('[data-kw-kit-tier]').forEach(function (row) {
      row.hidden = mine.indexOf(row.getAttribute('data-kw-kit-tier')) === -1;
    });

    if (d.pieces) {
      document.querySelectorAll('[data-kw-kit-count], [data-kw-stat-pieces]')
        .forEach(function (el) { el.textContent = d.pieces; });
    }

    var partsEl = document.querySelector('[data-kw-kit-parts]');
    if (partsEl && d.parts) partsEl.textContent = d.parts;

    var nowEl = document.querySelector('[data-kw-kit-now]');
    if (nowEl) nowEl.textContent = d.price;
  }

  function init(root) {
    var wrap = root.querySelector('[data-kw-tier]');
    var subBox = root.querySelector('[data-kw-sub]');
    var proxies = [].slice.call(document.querySelectorAll('[data-kw-cta-proxy]'));
    if (!wrap && !subBox && !proxies.length) return;
    if (root.dataset.kwTierReady === '1') return;
    root.dataset.kwTierReady = '1';

    var buy = root.querySelector('.kw-buy');
    if (buy && !buy.id) buy.id = 'KwBuy';

    var form = root.querySelector('form[action*="/cart/add"]');
    var idInput = form ? form.querySelector('input[name="id"]') : null;
    var priceEl = root.querySelector('[data-kw-price]');
    var wasEl = root.querySelector('[data-kw-was]');
    var saveEl = root.querySelector('[data-kw-save]');
    var ctaEl = root.querySelector('[data-kw-cta]');
    var sepWas = root.querySelector('[data-kw-sep-was]');
    var sepNow = root.querySelector('[data-kw-sep-now]');
    var sepBig = root.querySelector('[data-kw-sep-big]');
    var sepBox = root.querySelector('[data-kw-sep]');
    var summaryEl = root.querySelector('[data-kw-summary]');

    var subPlan = root.querySelector('[data-kw-sub-plan]');
    var subPrice = root.querySelector('[data-kw-sub-price]');
    var subWas = root.querySelector('[data-kw-sub-was]');
    var subBadge = root.querySelector('[data-kw-sub-badge]');
    var subNote = root.querySelector('[data-kw-sub-note]');
    var subRow = subBox ? subBox.closest('.kw-addon') : null;

    /* The delivery note under the button has two versions: one for a plain
       order, one for a basket carrying a subscription. Capture the first
       before anything overwrites it, or switching back has nothing to
       restore. */
    var shipEl = root.querySelector('[data-kw-ship]');
    var shipSub = shipEl ? shipEl.getAttribute('data-kw-ship-sub') : null;
    var shipBase = shipEl ? shipEl.innerHTML : null;

    var fmt = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: root.dataset.kwCurrency || 'GBP'
    });

    /* "£0" rather than "£0.00": shorter, and the column is a headline, not a
       ledger. Derived from the formatter so it carries the right symbol in
       whatever currency the market is presenting. */
    var ZERO = fmt.format(0).replace(/[.,]00$/, '');

    var radios = wrap ? [].slice.call(wrap.querySelectorAll('input[type="radio"]')) : [];
    var toggles = wrap ? [].slice.call(wrap.querySelectorAll('.kw-addon__in:not([data-kw-sub])')) : [];

    function currentRadio() {
      return wrap ? wrap.querySelector('input[type="radio"]:checked') : null;
    }

    function planOption() {
      if (!subPlan) return null;
      return subPlan.options[subPlan.selectedIndex] || null;
    }

    /* What the subscription adds to TODAY's charge. Zero on a plan whose first
       cycle is free — which is the whole point of that plan. */
    function subCents() {
      if (!subBox || !subBox.checked) return 0;
      var o = planOption();
      return o ? parseInt(o.dataset.price, 10) || 0 : 0;
    }

    /* The button quotes what the card gets charged today: the kit plus the
       first subscription payment. The recurring figure lives in the note below
       the switch, because those are two different promises. */
    function paintTotal() {
      var r = currentRadio();
      var base = r ? parseInt(r.dataset.priceRaw, 10) || 0
                   : parseInt(root.dataset.kwBase, 10) || 0;
      var sold = ctaEl ? ctaEl.disabled : false;
      var total = fmt.format((base + subCents()) / 100);

      if (ctaEl) {
        var span = ctaEl.querySelector('span');
        if (span) {
          span.textContent = sold ? 'Sold out'
            : (ctaEl.dataset.kwCta || 'Add to basket') + ' ' + MDASH + ' ' + total;
        }
      }
      proxies.forEach(function (p) {
        var s = p.querySelector('span');
        if (!s) return;
        s.textContent = sold ? 'Sold out'
          : (p.dataset.kwCta || 'Add to basket') + ' ' + MDASH + ' ' + total;
        p.setAttribute('aria-disabled', sold ? 'true' : 'false');
      });
    }

    function paintSub() {
      if (!subBox) return;
      var on = subBox.checked;
      if (subRow) subRow.classList.toggle('kw-addon--on', on);
      if (subPlan) subPlan.disabled = !on;

      var o = planOption();
      if (!o) { paintTotal(); return; }

      var today = parseInt(o.dataset.price, 10) || 0;
      var free = today === 0;

      /* The column answers one question, the same one the +£20 and +£30 rows
         above it answer: what does saying yes add to my basket right now? On a
         free first cycle that is nothing. It does NOT repeat the per-delivery
         price, which the dropdown two inches away already carries, and it does
         NOT carry the struck RRP, which above a £0 would read as a gift. */
      if (subPrice) subPrice.textContent = free ? ZERO + ' today' : '+' + o.dataset.money;
      if (subWas) {
        subWas.textContent = o.dataset.was || '';
        show(subWas, !free && !!o.dataset.was);
      }
      if (subBadge) {
        subBadge.textContent = free ? 'Ships ' + o.dataset.in : 'Subscription';
      }

      if (subNote) {
        if (!on) {
          subNote.textContent = '';
        } else if (free) {
          subNote.textContent = 'Nothing today ' + MDASH + ' no charge, and no refill. ' +
            'First refill ships ' + o.dataset.in + ': ' + o.dataset.recur + ' then, and ' +
            o.dataset.every + ' after. ' + SUB_TERMS;
        } else {
          subNote.textContent = o.dataset.recur + ' ' + o.dataset.every +
            ' after that. ' + SUB_TERMS;
        }
      }

      /* A basket with a subscription in it gets the subscription's delivery
         promise, not the one about spending over £50. */
      if (shipEl && shipSub) {
        if (on) shipEl.textContent = shipSub;
        else shipEl.innerHTML = shipBase;
      }

      paintTotal();
    }

    function apply(radio) {
      var d = radio.dataset;

      if (idInput) idInput.value = d.vid;
      if (priceEl) priceEl.textContent = d.price;

      show(wasEl, d.was !== '');
      if (wasEl && d.was !== '') wasEl.textContent = d.was;
      show(saveEl, d.save !== '');
      if (saveEl && d.save !== '') saveEl.textContent = 'Save ' + d.save;

      /* Liquid escapes the attribute, so a non-breaking space it writes before
         a separator can arrive here as the literal text "&nbsp;". Turn it back
         into the character it was meant to be. */
      if (summaryEl && d.summary) {
        summaryEl.textContent = d.summary.split('&nbsp;').join(NBSP);
      }

      if (sepBox) {
        show(sepBox, d.sep !== '');
        if (d.sep !== '') {
          if (sepWas) sepWas.textContent = d.sep;
          if (sepNow) sepNow.textContent = d.price;
          if (sepBig) sepBig.textContent = d.save;
        }
      }

      if (ctaEl) ctaEl.disabled = d.avail !== '1';

      /* Ask the gallery to swipe to this variant's image. It owns the track,
         the thumbnails and the scroll position; this file only names the
         media. A variant with no image of its own leaves the gallery alone
         rather than jumping back to the first slide. */
      if (d.media) {
        document.dispatchEvent(new CustomEvent('kw:media', { detail: { id: d.media } }));
      }

      syncDownstream(d);
      paintTotal();

      root.dataset.kwBase = d.priceRaw;
      root.dataset.kwPlans = d.plans || '{}';
      root.dispatchEvent(new CustomEvent('kw:variant', { bubbles: true }));

      try {
        var u = new URL(window.location.href);
        u.searchParams.set('variant', d.vid);
        window.history.replaceState({}, '', u);
      } catch (e) { /* older browsers simply keep the plain URL */ }
    }

    function radioForMask(mask) {
      for (var i = 0; i < radios.length; i++) {
        if (parseInt(radios[i].dataset.mask, 10) === mask) return radios[i];
      }
      return null;
    }

    /* Paint each row from its switch, and disable any switch whose resulting
       combination has no sellable variant behind it. */
    function paintToggles() {
      toggles.forEach(function (t) {
        var row = t.closest('.kw-addon');
        if (!row) return;
        row.classList.toggle('kw-addon--on', t.checked);

        var trial = 0;
        toggles.forEach(function (o) {
          var on = o === t ? !o.checked : o.checked;
          if (on) trial += parseInt(o.dataset.bit, 10) || 0;
        });
        var candidate = radioForMask(trial);
        var reachable = !!candidate && candidate.dataset.avail === '1';
        t.disabled = !reachable;
        row.classList.toggle('kw-addon--off', !reachable);
      });
    }

    function fromToggles() {
      var mask = 0;
      toggles.forEach(function (t) {
        if (t.checked) mask += parseInt(t.dataset.bit, 10) || 0;
      });
      var radio = radioForMask(mask);
      if (!radio) return;
      radio.checked = true;
      apply(radio);
      paintToggles();
    }

    radios.forEach(function (r) {
      r.addEventListener('change', function () { if (r.checked) apply(r); });
    });
    toggles.forEach(function (t) { t.addEventListener('change', fromToggles); });

    if (subBox) subBox.addEventListener('change', paintSub);
    if (subPlan) {
      subPlan.addEventListener('change', paintSub);
      /* The select sits inside the row; without this, using it would toggle
         the switch behind it. */
      subPlan.addEventListener('click', function (e) { e.stopPropagation(); });
    }

    /* Inline CTAs submit the real form rather than one of their own. */
    proxies.forEach(function (p) {
      p.addEventListener('click', function (e) {
        if (!form) return;
        if (p.getAttribute('aria-disabled') === 'true') return;
        e.preventDefault();
        if (form.requestSubmit) form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      });
    });

    /* ── Two lines in one add ──────────────────────────────
       Capture phase on the form, so this runs before Dawn's own submit
       handler and can stop it. Only when the subscription is switched on;
       otherwise Dawn's single-item path is left completely alone. */
    if (form && subBox) {
      form.addEventListener('submit', function (e) {
        if (!subBox.checked || !subPlan || !idInput) return;
        e.preventDefault();
        e.stopImmediatePropagation();

        var errWrap = root.querySelector('.product-form__error-message-wrapper');
        var errText = root.querySelector('.product-form__error-message');
        var spinner = ctaEl ? ctaEl.querySelector('.loading__spinner') : null;

        if (errWrap) errWrap.hidden = true;
        if (ctaEl) ctaEl.setAttribute('aria-disabled', 'true');
        if (spinner) spinner.classList.remove('hidden');

        var target = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        var body = {
          items: [
            { id: Number(idInput.value), quantity: 1 },
            { id: Number(subBox.dataset.vid), quantity: 1, selling_plan: Number(subPlan.value) }
          ]
        };
        if (target && typeof target.getSectionsToRender === 'function') {
          body.sections = target.getSectionsToRender().map(function (s) { return s.id; });
          body.sections_url = window.location.pathname;
        }

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify(body)
        })
          .then(function (r) { return r.json(); })
          .then(function (json) {
            if (json.status) {
              if (errText) errText.textContent = json.description || json.message || 'Could not add to basket.';
              if (errWrap) errWrap.hidden = false;
              return;
            }
            if (target && json.sections && typeof target.renderContents === 'function') {
              if (target.classList) target.classList.remove('is-empty');
              target.renderContents(json);
            } else {
              window.location = '/cart';
            }
          })
          .catch(function () { window.location = '/cart'; })
          .finally(function () {
            if (ctaEl) ctaEl.removeAttribute('aria-disabled');
            if (spinner) spinner.classList.add('hidden');
          });
      }, true);
    }

    /* Honour ?variant= on load so shared links and back-navigation land on the
       right configuration rather than silently resetting to the base kit. */
    if (radios.length) {
      var want = new URLSearchParams(window.location.search).get('variant');
      var target2 = null;
      if (want) {
        radios.forEach(function (r) { if (r.dataset.vid === want) target2 = r; });
      }
      if (!target2 || target2.disabled) {
        target2 = wrap.querySelector('input[type="radio"]:checked') || radios[0];
      }
      if (target2) {
        target2.checked = true;
        if (toggles.length) {
          var mask = parseInt(target2.dataset.mask, 10) || 0;
          toggles.forEach(function (t) {
            var bit = parseInt(t.dataset.bit, 10) || 0;
            t.checked = (mask & bit) === bit && bit !== 0;
          });
        }
        apply(target2);
        paintToggles();
      }
    }

    paintSub();
    paintTotal();
  }

  function boot() { document.querySelectorAll('[data-kw-plans]').forEach(init); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();